import { compact } from "lodash";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ArrowRight } from "@/assets/icons/icons";
import IndeterminateCheckbox from "@/assets/ux/IndeterminateCheckbox.svg?react";
import SelectedCheckbox from "@/assets/ux/SelectedCheckbox.svg?react";
import UnselectedCheckbox from "@/assets/ux/UnselectedCheckbox.svg?react";
import { TableHeader } from "@/components/Layout/Table/TableHeader/TableHeader";

import { ItemizedBillListIdForm } from "./ItemizedBillOverview";

const enum availableSortFields {
  PLAN = "PLAN",
  PERIOD = "PERIOD",
  AMOUNT = "AMOUNT",
  NUMBER = "NUMBER",
}

type SortDirection = "asc" | "desc";

export const ItemizedBillOverviewHeader = ({
  currentPageItemizedBillIds,
  onSortChange,
}: {
  currentPageItemizedBillIds: {
    serviceId: number;
    period: string;
  }[];
  onSortChange?: (field: availableSortFields, direction: SortDirection) => void;
}) => {
  const { t } = useTranslation("portal");
  const { watch, setValue } = useFormContext<ItemizedBillListIdForm>();

  const [sortField, setSortField] = useState<availableSortFields | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const currentSelectedItemizedBillIds = watch("selectedItems") ?? [];

  // Build encoded values for the current page (same format used in checkboxes)
  const currentPageValues = currentPageItemizedBillIds.map(
    ({ serviceId, period }) => `${serviceId}|${period}`,
  );

  // Count the selections on the current table page
  const currentPageSelectionCount = currentSelectedItemizedBillIds.filter(
    (value) => currentPageValues.includes(value),
  ).length;

  const headerCheckboxClick = () => {
    if (currentPageSelectionCount === currentPageValues.length) {
      const remainingSelections = currentSelectedItemizedBillIds.filter(
        (value) => !currentPageValues.includes(value),
      );

      setValue("selectedItems", remainingSelections);
    } else {
      const newSelections = [
        ...new Set([...currentSelectedItemizedBillIds, ...currentPageValues]),
      ];

      setValue("selectedItems", newSelections);
    }
  };

  const handleSort = (field: availableSortFields) => {
    // Determine new direction
    const newDirection: SortDirection =
      sortField === field ? (sortDirection === "asc" ? "desc" : "asc") : "asc";

    // Update state
    setSortField(field);
    setSortDirection(newDirection);

    // Trigger callback
    onSortChange?.(field, newDirection);
  };

  const renderSortIndicator = (field: availableSortFields) => (
    <ArrowRight
      className={`arrow h-4 w-4 transform transition-transform ${
        sortField === field
          ? sortDirection === "asc"
            ? "-rotate-90 opacity-100"
            : "rotate-90 opacity-100"
          : "opacity-0"
      }`}
    />
  );

  return (
    <TableHeader>
      <th className="bill-selection">
        <div className="flex items-center gap-2">
          <button
            className="bill-selector"
            title={t(
              currentPageSelectionCount > 0
                ? "bills.list.tableHeaders.deselectAllBills"
                : "bills.list.tableHeaders.selectAllBills",
            )}
            onClick={headerCheckboxClick}
          >
            {currentPageSelectionCount > 0 ? (
              <div className="has-selection-container">
                {
                  // Are ALL itemizedBills of the current page selected?
                  currentPageSelectionCount ===
                  currentPageItemizedBillIds.length ? (
                    <SelectedCheckbox className="checkbox" />
                  ) : (
                    <IndeterminateCheckbox className="checkbox" />
                  )
                }
              </div>
            ) : (
              <UnselectedCheckbox className="checkbox" />
            )}
          </button>
          <span className="text-sm text-primary-100">{`[${currentPageSelectionCount}/${currentPageValues.length}]`}</span>
        </div>
      </th>

      <th
        onClick={() => handleSort(availableSortFields.PLAN)}
        className="cursor-pointer"
      >
        <span className="flex">
          {t("bills.list.tableHeaders.plan")}
          {renderSortIndicator(availableSortFields.PLAN)}
        </span>
      </th>

      <th
        onClick={() => handleSort(availableSortFields.PERIOD)}
        className="cursor-pointer"
      >
        <span className="flex">
          {t("bills.list.tableHeaders.period")}
          {renderSortIndicator(availableSortFields.PERIOD)}
        </span>
      </th>

      <th
        onClick={() => handleSort(availableSortFields.AMOUNT)}
        className="cursor-pointer"
      >
        <span className="flex">
          {t("bills.list.tableHeaders.amount")}
          {renderSortIndicator(availableSortFields.AMOUNT)}
        </span>
      </th>

      <th
        onClick={() => handleSort(availableSortFields.NUMBER)}
        className="cursor-pointer"
      >
        <span className="flex">
          {t("bills.list.tableHeaders.number")}
          {renderSortIndicator(availableSortFields.NUMBER)}
        </span>
      </th>
    </TableHeader>
  );
};
